import { Component, HostListener, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Dictionary } from 'guest-app-ui';
import { AppState } from 'src/app/app.state';
import { DynamicFormChangedEvent } from 'src/app/feature/dynamic-form/dynamic-form.component';
import { DynamicFormControl } from 'src/app/feature/dynamic-form/input/dynamic-form-control';
import { validString } from 'src/app/shared/utilities/types.utils';
import { routeTo } from '../routing/routing.actions';
import { closeNotificationAction } from './notification.actions';
import { ClickType, Notification } from './notification.state';
import { NotificationFocusService } from '../services/notification-focus.service';

const matches = HTMLElement.prototype.matches || HTMLElement.prototype.webkitMatchesSelector;

@Component({
	selector: 'ahp-notification',
	templateUrl: './notification.component.html',
	styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent implements OnInit {
	/**
	 * Our input for the notification to display
	 */
	@Input() notify: Notification;
	/**
	 * Our click types
	 */
	public clickType = ClickType;
	/**
	 * is the dialog closing
	 */
	public closing: boolean;
	/**
	 * is the input field valid?
	 */
	public isInputValid: boolean;
	/**
	 * Do we want to show the input field?
	 */
	public showInput: boolean;
	/**
	 * Optional form controls
	 */
	public controls: DynamicFormControl<any>[][] = [];
	/**
	 * form data
	 */
	private _formData: Dictionary<any> = null;
	/**
	 * Our host listener
	 * @param event The event
	 */
	@HostListener('click', ['$event.target'])
	didClickElement(el: HTMLElement) {
		if (matches.call(el, '.modal__link')) {
			const route = (el.dataset.ahpHref || '').split('/');
			[
				routeTo({ payload: route }),
				closeNotificationAction({
					clicked: ClickType.CANCEL,
				}),
			].forEach((action) => this._store$.dispatch(action));
		}
	}

	constructor(
		private _domSanitizer: DomSanitizer,
		private _store$: Store<AppState>,
		private _notificationFocusService: NotificationFocusService
	) {
		this.setFocus();
	}

	ngOnInit(): void {
		if (!!this.notify?.inputField) {
			this.showInput = true;
			this.controls.push([this.notify.inputField]);
		} else {
			this.showInput = false;
		}
	}

	/**
	 * handle dynamic form validity changes
	 */
	public inputValidity(change: DynamicFormChangedEvent) {
		this.isInputValid = change.valid;
		this._formData = this.isInputValid ? change.value : null;
	}

	/**
	 * Sets the focus to the correct button
	 */
	setFocus(): void {
		this._notificationFocusService.setBlockService(true);
		setTimeout(() => {
			let btnToFocus: string;
			if (this.notify.dialogType) {
				btnToFocus =
					this.notify.dialogType === 'CONFIRM' && !this.notify?.hideCancelButton
						? 'modal-cancel'
						: 'modal-confirm';
			} else {
				btnToFocus = 'modal-confirm';
			}
			document.getElementById(btnToFocus).focus();
			this._notificationFocusService.setBlockService(false);
			this.trapFocus(document.getElementById('modalButtons'));
		}, 15);
	}

	/**
	 * Traps the focus within the modal
	 * @param element The element to trap focus in
	 */
	trapFocus(element): void {
		this._notificationFocusService.setBlockService(true);
		const focusableEls = [document.querySelector('#modal-confirm')];
		/* Add the cancel button if it exists */
		if (this.notify.dialogType === 'CONFIRM') {
			focusableEls.unshift(document.querySelector('#modal-cancel'));
		}
		const firstFocusableEl = focusableEls[0];
		const lastFocusableEl = focusableEls[focusableEls.length - 1];
		const KEYCODE_TAB = 9;

		element.addEventListener('keydown', (e) => {
			const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

			if (!isTabPressed) {
				return;
			}

			if (e.shiftKey) {
				/* shift + tab */
				if ((document.activeElement as HTMLElement) === firstFocusableEl) {
					(lastFocusableEl as HTMLElement).focus();
					e.preventDefault();
				}
			} else {
				/* tab */
				if ((document.activeElement as HTMLElement) === lastFocusableEl) {
					(firstFocusableEl as HTMLElement).focus();
					e.preventDefault();
				}
			}
			this._notificationFocusService.setBlockService(false);
		});
	}

	/**
	 * Builds a link to use with the regexp replacement
	 * @param href The string to use.
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/link_role} for accessibility information
	 */
	buildLink(href: string): string {
		return `<a class='modal__link cursor--pointer gap-text--secondary' aria-role="link" tabindex="0" data-ahp-href='${href}'>$1</a>`;
	}

	/**
	 * Called when a modal button is clicked.
	 * @param clicked The type of button clicked
	 */
	closeNotification(clicked: ClickType): void {
		this.closing = true;
		const data = this._formData;

		this._store$.dispatch(
			closeNotificationAction({
				clicked,
				data,
			})
		);
		this._notificationFocusService.returnFocus();
	}

	/**
	 * Takes a string and replaces certain syntax with links to various parts of the site.
	 * @param message The string to do replacement in.
	 */
	addLinksToNotification(message: string): SafeHtml {
		if (!validString(message)) {
			return message;
		}

		const newMessage = message
			.replace(/<<OVERVIEW_LINK>>(.+?)<<OVERVIEW_LINK>>/g, this.buildLink('account/overview'))
			.replace(/<<PAYMENTS_LINK>>(.+?)<<PAYMENTS_LINK>>/g, this.buildLink('account/payment'))
			.replace(/<<HISTORY_LINK>>(.+?)<<HISTORY_LINK>>/g, this.buildLink('account/history'))
			.replace(/<<FAQ_LINK>>(.+?)<<FAQ_LINK>>/g, this.buildLink('account/faq'))
			.replace(/<<MEMBERSHIP_LINK>>(.+?)<<MEMBERSHIP_LINK>>/g, this.buildLink('account/membership'));

		return this._domSanitizer.bypassSecurityTrustHtml(newMessage);
	}
}
